type ErrorDetails = {
  code?: unknown
  message?: unknown
  name?: unknown
  reason?: unknown
}

type ErrorCandidate = ErrorDetails & {
  details?: ErrorDetails
  error?: ErrorDetails
  response?: {
    data?: {
      details?: ErrorDetails
      error?: ErrorDetails
      message?: unknown
    }
  }
  sipStatusCode?: unknown
  statusCode?: unknown
}

const asText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : ''

const getCandidate = (error: unknown): ErrorCandidate =>
  error && typeof error === 'object' ? (error as ErrorCandidate) : {}

export const getSoftphoneErrorCode = (error: unknown): string => {
  const candidate = getCandidate(error)
  return asText(
    candidate.details?.code ||
      candidate.error?.code ||
      candidate.response?.data?.details?.code ||
      candidate.response?.data?.error?.code ||
      candidate.code
  ).toLowerCase()
}

export const getSoftphoneErrorText = (error: unknown): string => {
  const candidate = getCandidate(error)
  const values = [
    candidate.details?.message,
    candidate.details?.reason,
    candidate.error?.message,
    candidate.error?.reason,
    candidate.response?.data?.details?.message,
    candidate.response?.data?.details?.reason,
    candidate.response?.data?.error?.message,
    candidate.response?.data?.error?.reason,
    candidate.response?.data?.message,
    candidate.message,
    typeof error === 'string' ? error : '',
  ]

  return values.map(asText).find(Boolean) || ''
}

const getSipStatusCode = (error: unknown): number | undefined => {
  const candidate = getCandidate(error)
  const status = Number(candidate.sipStatusCode || candidate.statusCode)
  return Number.isFinite(status) ? status : undefined
}

export const getSoftphoneCallErrorMessage = (error: unknown): string => {
  const code = getSoftphoneErrorCode(error)
  const originalMessage = getSoftphoneErrorText(error)
  const text = originalMessage.toLowerCase()
  const sipStatus = getSipStatusCode(error)

  if (code === 'dispatcher_off_duty' || text.includes('active shift')) {
    return 'You are not on an active shift. Clock in to a current shift, then try the call again.'
  }

  if (
    code === 'endpoint_disabled' ||
    code === 'softphone-deployment-disabled' ||
    text.includes('endpoint is disabled') ||
    text.includes('deployment is disabled')
  ) {
    return 'Softphone calling is disabled for your account. Contact a supervisor to enable it.'
  }

  if (
    code === 'missing_issuer' ||
    code === 'invalid_issuer' ||
    code === 'claims_missing' ||
    code === 'user_missing' ||
    text.includes('session has expired')
  ) {
    return 'Your session cannot authorize calls. Sign out, sign in again, and retry.'
  }

  if (
    code === 'not_registered' ||
    code === 'not_connected' ||
    text.includes('softphone is not connected') ||
    text.includes('softphone is still connecting')
  ) {
    return 'The softphone is not connected to the call server. Select Retry, wait for “Softphone ready,” then call again.'
  }

  if (
    text.includes('notallowederror') ||
    text.includes('permission denied') ||
    text.includes('microphone access')
  ) {
    return 'Microphone access is blocked. Allow microphone access for this site, then retry the call.'
  }

  if (text.includes('notfounderror') || text.includes('requested device')) {
    return 'No microphone was found. Connect or enable a microphone, then retry the call.'
  }

  if (text.includes('notreadableerror') || text.includes('device in use')) {
    return 'The microphone is unavailable or being used by another app. Close the other app, then retry.'
  }

  if (sipStatus === 486 || text.includes('486 busy')) {
    return 'The destination is busy. Wait a moment and try again.'
  }

  if (
    [404, 410, 484, 604].includes(sipStatus || 0) ||
    text.includes('no route') ||
    text.includes('does not exist anywhere')
  ) {
    return 'The call network could not route this number. Check the number and country code, then retry.'
  }

  if (
    [408, 480].includes(sipStatus || 0) ||
    text.includes('temporarily unavailable')
  ) {
    return 'The destination did not respond or is temporarily unavailable. Try again shortly.'
  }

  if (sipStatus === 503 || text.includes('503 service unavailable')) {
    return 'The external call provider is temporarily unavailable. Try again shortly or contact support.'
  }

  if (
    originalMessage &&
    !['unable to start call', 'error starting call'].includes(text)
  ) {
    return originalMessage
  }

  return 'The call could not be started. Check that you are on shift and the softphone shows “Softphone ready,” then retry.'
}
